import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { usePortalPosition } from '@/hooks/usePortalPosition';

import './LocationCombobox.css';

export type LocationOption = {
  /** Stable identifier from DLD (community_key / building_key / zone_key).
   * Used as React key and for deduplication on the server side. */
  key: string;
  /** Canonical DLD display name. The combobox writes this verbatim into the
   * form value — guarantees matching against `dld_transactions` later. */
  displayName: string;
  /** Optional second line under the option label (zone for communities,
   * community for buildings). */
  secondary?: string;
  /** Optional transaction count rendered as a small right-side counter. */
  count?: number;
  /** Optional canonical key of the parent community. Set when the option
   * represents a building; used by `onCommitOption` callers to auto-fill
   * the community form field upstream (cascading hierarchy). */
  communityKey?: string;
  /** Optional canonical display name of the parent community (idem). */
  communityDisplayName?: string;
  /** Optional canonical key of the parent zone. Set when the option
   * represents a community OR a building; used by `onCommitOption` callers
   * to auto-fill the zone form field upstream. */
  zoneKey?: string;
  /** Optional canonical display name of the parent zone (idem). */
  zoneDisplayName?: string;
};

export type LocationComboboxProps = {
  name: string;
  label: string;
  /** Controlled value — the canonical DLD display_name string. Pass `''` for
   * empty. */
  value: string;
  onChange: (value: string) => void;
  /** Async fetcher called on every (debounced) keystroke. Empty query should
   * return the top N most popular options. Errors surface as `noResults` UX. */
  onSearch: (query: string) => Promise<LocationOption[]>;
  /** Optional in-input placeholder. */
  placeholder?: string;
  required?: boolean;
  error?: string;
  helper?: string;
  /** When false, the "type my own value" button is hidden — the user must
   * pick from the list (strict mode). Default true (admin form). */
  allowFreeText?: boolean;
  disabled?: boolean;
  /** Debounce delay in ms before firing `onSearch`. Default 250. */
  debounceMs?: number;
  /** Optional render override for individual options (e.g. custom rich rows).
   * Falls back to the standard `<displayName> <secondary> <count>` layout. */
  renderOption?: (option: LocationOption, active: boolean) => ReactNode;
  /** Fires when the user commits an option from the picker (click or
   * Enter). Receives the full `LocationOption` so the parent can read
   * metadata (`zoneKey`, `communityKey`, `*DisplayName`) and auto-fill
   * higher-level form fields. NOT called on free-text blur — only on
   * picker commit. Used by the 3-level cascading combobox (PR-3). */
  onCommitOption?: (option: LocationOption) => void;
};

const DEFAULT_DEBOUNCE_MS = 250;

/**
 * Server-driven combobox for DLD locations (communities + buildings).
 * Wraps a debounced async `onSearch` and renders an aria-compliant
 * combobox + listbox. The committed value is always the canonical DLD
 * `display_name` string so it matches `dld_transactions` 1-to-1 at
 * compare time.
 *
 * Two interaction modes coexist:
 *   - Picker mode (default) — dropdown of remote suggestions, sticky
 *     `[Type my own value]` button at the bottom that lets the user opt out.
 *   - Free-text mode — the input becomes a plain text field with no
 *     dropdown. Useful for brand-new buildings that have no DLD transaction
 *     yet. A small "Unverified" badge is rendered next to the label.
 *
 * Designed to be plugged into `react-hook-form` via `<Controller>`:
 *   <Controller
 *     name="community"
 *     control={form.control}
 *     render={({ field, fieldState }) => (
 *       <LocationCombobox
 *         {...field}
 *         label={t('community')}
 *         error={fieldState.error?.message}
 *         onSearch={(q) => searchDldCommunities(q).then(toOptions)}
 *       />
 *     )}
 *   />
 */
export function LocationCombobox({
  name,
  label,
  value,
  onChange,
  onSearch,
  placeholder,
  required,
  error,
  helper,
  allowFreeText = true,
  disabled,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  renderOption,
  onCommitOption,
}: LocationComboboxProps) {
  const { t } = useTranslation();
  const generatedId = useId();
  const id = `${name}-${generatedId}`;
  const listboxId = `${id}-listbox`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const unverifiedId = `${id}-unverified`;
  const optionId = (i: number) => `${id}-opt-${i}`;

  // The input always reflects the current value (mirrors the picker
  // result OR the free-text manual entry). When the user is actively
  // typing, the input drives the search query — but the committed value
  // is only updated on selection (picker) or on blur (free-text).
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [freeText, setFreeText] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);
  const requestSeqRef = useRef(0);

  // Keep the input mirror in sync if the parent updates `value` externally
  // (e.g. form.reset). Avoids stale local state after a Discard draft.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setActiveIdx(-1);
  }, []);

  // Click outside closes the dropdown without resetting the typed value
  // (the user may want to keep editing — only blur to commit free-text).
  // We also accept clicks inside the portaled listbox: since it now lives
  // on document.body, `rootRef.contains(target)` returns false even for an
  // intentional pick. Including `listboxRef` in the check lets the click
  // path complete normally before the dropdown closes.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (rootRef.current.contains(target)) return;
      if (listboxRef.current && listboxRef.current.contains(target)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closeDropdown]);

  // Debounced search. Each keystroke schedules a fresh fetch; older
  // in-flight requests are discarded by sequence number so a slow earlier
  // response never clobbers a fresh result.
  const runSearch = useCallback(
    (q: string) => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      onSearch(q)
        .then((rows) => {
          if (requestSeqRef.current !== seq) return; // stale
          setResults(rows);
          setActiveIdx(rows.length > 0 ? 0 : -1);
        })
        .catch(() => {
          if (requestSeqRef.current !== seq) return;
          setResults([]);
          setActiveIdx(-1);
        })
        .finally(() => {
          if (requestSeqRef.current === seq) setLoading(false);
        });
    },
    [onSearch],
  );

  // When the dropdown opens, kick off an empty-query search so the user
  // sees the top N options before typing anything.
  useEffect(() => {
    if (!open || freeText) return;
    runSearch(query);
    // Intentionally not depending on `query` here: re-fetches on text input
    // are handled by the debounced effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, freeText]);

  // Debounced effect on `query` while the picker is open.
  useEffect(() => {
    if (!open || freeText) return;
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      runSearch(query);
    }, debounceMs);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [query, open, freeText, debounceMs, runSearch]);

  const commitOption = useCallback(
    (option: LocationOption) => {
      onChange(option.displayName);
      // Fire onCommitOption AFTER onChange so a parent setValue('zone', ...)
      // can rely on the current field being already updated. Synchronous —
      // both setValue calls land in the same React batch.
      onCommitOption?.(option);
      setQuery(option.displayName);
      closeDropdown();
      inputRef.current?.blur();
    },
    [onChange, onCommitOption, closeDropdown],
  );

  const switchToFreeText = useCallback(() => {
    setFreeText(true);
    closeDropdown();
    // Move focus back to the input so the user can keep typing where they
    // left off.
    inputRef.current?.focus();
  }, [closeDropdown]);

  const switchToPicker = useCallback(() => {
    setFreeText(false);
    setOpen(true);
    setQuery(value);
  }, [value]);

  const onInputFocus = () => {
    if (disabled) return;
    if (!freeText) setOpen(true);
  };

  const onInputBlur = () => {
    if (freeText) {
      const trimmed = query.trim();
      if (trimmed !== value) onChange(trimmed);
      return;
    }
    // Picker mode: when `allowFreeText` is true (default — admin form,
    // seller building), accept the typed query on blur even if the user
    // did not click an option. This makes the field behave like a TextField
    // when there is no exact match in the DLD referential, while the strict
    // mode (allowFreeText=false — seller community) still requires an
    // explicit pick (the parent's Zod validation will surface the empty
    // value to the user).
    if (allowFreeText) {
      const trimmed = query.trim();
      if (trimmed !== value) onChange(trimmed);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQuery(next);
    if (freeText) {
      // In free-text mode, every keystroke commits — the picker dropdown
      // stays closed. Trim is delayed to onBlur so the user can type
      // intermediate whitespace.
      onChange(next);
    } else if (!open) {
      // Opening the dropdown automatically on the first keystroke gives
      // the user an immediate feedback loop.
      setOpen(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || freeText) return;
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIdx((i) => (results.length === 0 ? -1 : (i + 1) % results.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIdx((i) =>
          results.length === 0 ? -1 : (i - 1 + results.length) % results.length,
        );
        break;
      case 'Enter': {
        event.preventDefault();
        const opt = activeIdx >= 0 ? results[activeIdx] : undefined;
        if (opt) commitOption(opt);
        break;
      }
      case 'Tab':
        closeDropdown();
        break;
      default:
        break;
    }
  };

  const describedBy = useMemo(() => {
    return (
      [error ? errorId : null, !error && helper ? helperId : null, freeText ? unverifiedId : null]
        .filter(Boolean)
        .join(' ') || undefined
    );
  }, [error, errorId, helper, helperId, freeText, unverifiedId]);

  const { panelStyle } = usePortalPosition({
    triggerRef: inputRef,
    open: open && !freeText,
    matchTriggerWidth: true,
    onTriggerOutOfView: closeDropdown,
  });

  const dropdownContent =
    open && !freeText ? (
      <ul
        ref={listboxRef}
        id={listboxId}
        role="listbox"
        className="location-combobox__listbox"
        style={panelStyle}
        tabIndex={-1}
      >
        {loading && results.length === 0 ? (
          <li className="location-combobox__placeholder" role="presentation">
            {t('locationCombobox.loading')}
          </li>
        ) : results.length === 0 ? (
          <li className="location-combobox__placeholder" role="presentation">
            {t('locationCombobox.noResults')}
          </li>
        ) : (
          results.map((opt, i) => {
            const active = i === activeIdx;
            const selected = opt.displayName === value;
            return (
              <li
                key={opt.key}
                id={optionId(i)}
                role="option"
                aria-selected={selected}
                className={[
                  'location-combobox__option',
                  active ? 'location-combobox__option--active' : null,
                  selected ? 'location-combobox__option--selected' : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => {
                  // Prevent the input's onBlur from firing before onClick
                  // (otherwise commitOption races with closeDropdown).
                  e.preventDefault();
                }}
                onClick={() => commitOption(opt)}
              >
                {renderOption ? (
                  renderOption(opt, active)
                ) : (
                  <>
                    <span className="location-combobox__option-primary">{opt.displayName}</span>
                    {opt.secondary ? (
                      <span className="location-combobox__option-secondary">{opt.secondary}</span>
                    ) : null}
                    {/* `opt.count` is intentionally not rendered — the value
                     * is still fetched from the RPC and drives the server-side
                     * `ORDER BY transaction_count DESC`, but it's noise in
                     * the picker UI. Keep the field on `LocationOption` so a
                     * future `renderOption` consumer can surface it (e.g.
                     * Settings panel) without a schema change. */}
                  </>
                )}
              </li>
            );
          })
        )}
        {allowFreeText ? (
          <li
            className="location-combobox__footer"
            role="presentation"
            onMouseDown={(e) => e.preventDefault()}
          >
            <button
              type="button"
              className="location-combobox__free-text-cta"
              onClick={switchToFreeText}
            >
              {t('locationCombobox.useFreeText')}
            </button>
          </li>
        ) : null}
      </ul>
    ) : null;

  return (
    <div
      ref={rootRef}
      className={[
        'm5-field',
        'location-combobox',
        error ? 'm5-field--error' : null,
        disabled ? 'location-combobox--disabled' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      onKeyDown={onKeyDown}
    >
      <label className="m5-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="m5-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
        {freeText ? (
          <span id={unverifiedId} className="location-combobox__badge">
            {t('locationCombobox.unverified')}
          </span>
        ) : null}
      </label>
      <div className="m5-field__control location-combobox__control">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-activedescendant={open && activeIdx >= 0 ? optionId(activeIdx) : undefined}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={onInputChange}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          className="m5-field__input location-combobox__input"
        />
        {dropdownContent && typeof document !== 'undefined'
          ? createPortal(dropdownContent, document.body)
          : null}
      </div>
      {error ? (
        <span id={errorId} className="m5-field__error" role="alert">
          {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="m5-field__helper">
          {helper}
        </span>
      ) : freeText ? (
        <div className="location-combobox__free-text-helper">
          <span id={unverifiedId} className="m5-field__helper">
            {t('locationCombobox.unverifiedHelper')}
          </span>
          <button type="button" className="location-combobox__switch-back" onClick={switchToPicker}>
            {t('locationCombobox.switchBackToPicker')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
